import { useState } from 'react';
import styled, { css } from 'styled-components';
import type { PositionValue } from '@elfsight/configurator-contracts';
import { Show } from '@elfsight/lib-utils';
import { Portal } from '@elfsight/widget-modal';
import { WidgetBackground } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { useRect } from 'react-use-rect';
import { usePopup, useThumbnailMode } from '../../../../utils';
import { Deactivation } from '../../../Deactivation';
import { Content } from '../../content';
import { FREE_LINK_HEIGHT } from '../../free-link';
import { BarCloseControl } from './components';
import type { LayoutVariationProps } from './types';

export function LayoutBar({
  show,
  position,
  controls: { closeButtonVisible, closeButtonColor },
  ariaLabel,
  ...forwardingProps
}: LayoutVariationProps) {
  const { widgetBackground, displayFreeLink } = useSettings();
  const { styles: thumbnailStyles } = useThumbnailMode({ withWidth: true });

  const customBackground = { ...widgetBackground, borderRadius: 0 };

  const { close } = usePopup();

  const [contentHeight, setContentHeight] = useState(0);
  const [contentRef] = useRect(({ height }) => setContentHeight(height));

  const centeringCloseButton = contentHeight <= 80;

  if (!show) {
    return null;
  }

  return (
    <Portal containerElement={document.body}>
      <BarContent
        $position={position}
        ref={contentRef}
        aria-label={ariaLabel}
        tabIndex={-1}
        role="dialog"
        aria-modal
        style={thumbnailStyles}
      >
        <Show when={!!closeButtonVisible}>
          <WrapperButton $centeringCloseButton={centeringCloseButton}>
            <BarCloseControl onClick={close} color={closeButtonColor} />
          </WrapperButton>
        </Show>

        <Deactivation>
          <WidgetBackground {...customBackground}>
            <Wrapper $position={position} $displayFreeLink={!!displayFreeLink}>
              <Content popupShowing={show} {...forwardingProps} />
            </Wrapper>
          </WidgetBackground>
        </Deactivation>
      </BarContent>
    </Portal>
  );
}

const BarContent = styled.div<{ $position: PositionValue }>`
  position: fixed;
  z-index: 99999999;
  left: 0;
  width: 100%;
  min-width: 100%;
  ${({ $position }) => ($position === 'top' ? 'top: 0' : 'bottom: 0')};
`;

const Wrapper = styled.div<{
  $position: PositionValue;
  $displayFreeLink: boolean;
}>`
  ${({ $position, $displayFreeLink }) => css`
    display: flex;
    justify-content: center;
    position: relative;
    ${$displayFreeLink &&
    css`
      padding-block: ${$position === 'top'
        ? `${FREE_LINK_HEIGHT}px 0`
        : `0 ${FREE_LINK_HEIGHT}px`};
    `}
  `}
`;

const WrapperButton = styled.div<{ $centeringCloseButton: boolean }>`
  display: flex;
  right: 0;
  position: absolute;
  margin-right: 12px;
  width: 44px;
  z-index: 99999999;
  margin-top: ${({ $centeringCloseButton }) =>
    $centeringCloseButton ? 0 : 12}px;
  ${({ $centeringCloseButton }) =>
    $centeringCloseButton
      ? { top: '50%', transform: 'translate(0, -50%)' }
      : {}};
`;
